import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Minimal text logo component - Exor/LMDV style
function Logo({ light = false }) {
  return (
    <div className={`flex flex-col items-start leading-none ${light ? 'text-white' : 'text-[#0A192F]'}`}>
      <span className="text-2xl font-light tracking-[0.15em]">BNDV</span>
      <span className="text-[9px] uppercase tracking-[0.35em] opacity-60 mt-0.5">Capital</span>
    </div>
  );
}

export default function Navbar() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/about', label: 'About' },
    { path: '/portfolio', label: 'Portfolio' },
    { path: '/governance', label: 'Governance' },
    { path: '/investor-relations', label: 'Investor Relations' },
    { path: '/news', label: 'News' },
    { path: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;
  const isHome = location.pathname === '/';
  const showWhite = isHome && !scrolled;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${showWhite ? 'bg-transparent' : 'bg-white/95 backdrop-blur-md border-b border-slate-100 shadow-sm'}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <Logo light={showWhite} />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}-link`}
                className={`text-[13px] uppercase tracking-wider transition-colors ${
                  isActive(link.path)
                    ? showWhite ? 'text-white font-medium' : 'text-[#0A192F] font-medium'
                    : showWhite ? 'text-white/80 hover:text-white' : 'text-slate-500 hover:text-[#0A192F]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`lg:hidden p-2 ${showWhite ? 'text-white' : 'text-[#0A192F]'}`}
            data-testid="mobile-menu-button"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-slate-100"
          >
            <div className="px-6 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileOpen(false)}
                  className={`block text-sm uppercase tracking-wider ${
                    isActive(link.path) ? 'text-[#0A192F] font-medium' : 'text-slate-500'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
